/**
 * Kanban Task Tracker — API Client
 * Client unifié pour tous les appels backend Kanban.
 */

const BASE = '/kanban/api';

export type TaskStatus = 'todo' | 'in_progress' | 'review' | 'done';
export type TaskSource = 'dify' | 'opencode' | 'supercoldbuilder' | 'manual';
export type Status = TaskStatus;
export type Source = TaskSource;

export interface Project {
  id: number;
  name: string;
  description: string | null;
  color: string | null;
  created_at: string;
  updated_at: string;
}

export interface Task {
  id: number;
  project_id: number;
  title: string;
  description: string | null;
  status: TaskStatus;
  position: number;
  session_link: string | null;
  source: TaskSource | null;
  created_at: string;
  updated_at: string;
}

export interface Stats {
  by_status: Record<TaskStatus, number>;
  total: number;
  done: number;
}

async function request<T = any>(url: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  const res = await fetch(BASE + url, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ── Projects ──

export function getProjects(): Promise<Project[]> {
  return request('/projects');
}

export function createProject(name: string, description?: string, color?: string): Promise<Project> {
  return request('/projects', {
    method: 'POST',
    body: JSON.stringify({ name, description, color }),
  });
}

export function updateProject(id: number, data: Partial<Project>): Promise<Project> {
  return request(`/projects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function deleteProject(id: number): Promise<null> {
  return request(`/projects/${id}`, { method: 'DELETE' });
}

// ── Tasks ──

export function getTasks(projectId: number, status?: TaskStatus): Promise<Task[]> {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (status) params.set('status', status);
  return request(`/tasks?${params.toString()}`);
}

export function createTask(data: {
  project_id: number;
  title: string;
  description?: string;
  status?: TaskStatus;
  session_link?: string;
  source?: TaskSource;
}): Promise<Task> {
  return request('/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateTask(id: number, data: Partial<Task>): Promise<Task> {
  return request(`/tasks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function deleteTask(id: number): Promise<null> {
  return request(`/tasks/${id}`, { method: 'DELETE' });
}

export function moveTask(id: number, status: TaskStatus, position?: number): Promise<Task> {
  return request(`/tasks/${id}/move`, {
    method: 'PATCH',
    body: JSON.stringify({ status, position }),
  });
}

export function reorderTasks(
  projectId: number,
  status: TaskStatus,
  orderedIds: number[],
): Promise<{ status: string; reordered: number }> {
  return request('/tasks/reorder', {
    method: 'PATCH',
    body: JSON.stringify({ project_id: projectId, status, ordered_ids: orderedIds }),
  });
}

export function getStats(): Promise<Stats> {
  return request('/stats');
}
